import { useI18n } from "vue-i18n";
import type { Property } from "@/modules/quotes/api/graphql/types";
import { isRef, type Ref } from "vue";
import {
  CUSTOM_PROPERTY_PRICE_UNIT_NAME,
  CUSTOM_PROPERTY_PRICE_UNIT_VALUE,
  CUSTOM_PROPERTY_GROUP_NAME,
} from "@/shared/common/constants";
import type { Product, VariationType } from "@/core/api/graphql/types";

// Innovadis custom: Get property value, accepts both Ref<Property[]> and plain Property[]
export function getCustomPropertyValue(
  properties: Ref<Property[] | undefined> | Property[] | undefined,
  propertyName: string,
): Property["value"] {
  const resolved = isRef(properties) ? properties.value : properties;
  return resolved?.find((prop) => prop.name === propertyName)?.value;
}

// Innovadis custom: Get product name in the right format
export function getProductName(
  product: Product | undefined,
  addGroupName: boolean = false,
  ItemName: string | undefined = undefined,
): string {
  if (!product) {
    return "";
  }

  // In cart the item.product?.name has no value, we provide the name via item.name
  let productName = product.name;
  if (!productName && ItemName) {
    productName = ItemName;
  }

  // for article groups always return the name
  // for variations by default return the name
  if (product.hasVariations || !addGroupName) {
    return productName;
  }

  // when there is no article group context we need the name prefixed by the article group name
  const groupName = getCustomPropertyValue(product.properties, CUSTOM_PROPERTY_GROUP_NAME);
  return groupName ? `${groupName}, ${productName}` : productName;
}

// Innovadis custom
// Tries to get info from min variation, or from properties
export function getProductPriceUnitText(product: Product | undefined): string {
  // if either unit of measure or price unit value in min variation price has no value, try to get info from properties
  if (
    !product?.hasVariations || // should have variations, or there is no min variation price
    !product?.minVariationPrice?.priceUnit || // should have a unit price
    product.minVariationPrice.priceUnit <= 0 || // should have a unit price with a value greater than 0
    !product?.minVariationPrice?.unitOfMeasure || // should have a unit of measure
    product.minVariationPrice.unitOfMeasure.length <= 0 // should habe a unit of measure value
  ) {
    return getPriceProductUnitTextFromProperties(product?.properties);
  }

  // return translated min variation values
  const { t } = useI18n();
  return t("common.labels.price_unit_text", {
    priceUnitValue: product.minVariationPrice.priceUnit.toString(),
    priceUnitName: product.minVariationPrice.unitOfMeasure,
  });
}

// Innovadis custom
// Tries to get info from properties
// This fallback is still nescessary unfortunately, because the variants are not extendable a the moment and orderline products have no minvariation info
export function getPriceProductUnitTextFromProperties(
  properties: Ref<Property[] | undefined> | Property[] | undefined,
): string {
  const { t } = useI18n();
  const priceUnitValue = parseInt(
    getCustomPropertyValue(properties, CUSTOM_PROPERTY_PRICE_UNIT_VALUE)?.toString() ?? "",
  );

  // if price unit value is empty return empty string
  if (!priceUnitValue || priceUnitValue <= 0) {
    return "";
  }

  // if price unit name is empty use default: pieces
  const priceUnitName =
    getCustomPropertyValue(properties, CUSTOM_PROPERTY_PRICE_UNIT_NAME) ??
    t("common.labels.price_unit_default_unit_name");

  // return translated property values
  return t("common.labels.price_unit_text", {
    priceUnitValue: priceUnitValue.toString(),
    priceUnitName: priceUnitName,
  });
}

// Innovadis custom
// Gets a translated sales unit text which is shown in the checkout and all places with add to cart
export function getProductSalesUnitText(product: Product | undefined): string {
  const packSize = product?.packSize ?? 0;

  if (packSize <= 0) {
    return "";
  }

  const { t } = useI18n();
  return t("common.labels.sales_unit_text", {
    packSize: packSize.toString(),
  });
}

// Innovadis custom
// Gets the number value of the unit factor from the product properties (Є10,00 per 50 pieces ==> 50)
// Used for passing info to analytics, without this value the price values are useless
export function getProductUnitFactorValue(product: Product | VariationType | undefined): number | undefined {
  const propertyValue = parseInt(
    getCustomPropertyValue(product?.properties, CUSTOM_PROPERTY_PRICE_UNIT_VALUE)?.toString() ?? "",
  );
  return propertyValue > 0 ? propertyValue : undefined;
}

// Innovadis custom
// BB2B-149 Against design the number format should be 1.234,56 for all cultures
export function getBossardFormattedNumber(amount: number) {
  return new Intl.NumberFormat("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}
